import {type ColumnPreferenceColumn} from '@qep/shared/src';

export type ColumnPreferenceApp = {
  id: string;
  label: string;
  description: string;
  columns: ColumnPreferenceColumn[];
};

const column = (id: string, label: string, visible = true): ColumnPreferenceColumn => ({
  id,
  label,
  visible,
});

const columnPreferenceAppDefinitions: ColumnPreferenceApp[] = [
  {
    id: 'orderEntry',
    label: 'Order Entry 1.0',
    description: 'Basket order ticket defaults',
    columns: [
      column('direction', 'Side'),
      column('instrument', 'Product'),
      column('quantity', 'Qty'),
      column('strategy', 'Strategy'),
      column('description', 'Description'),
      column('prices.snapshot.bid.size', 'Bid Size'),
      column('prices.snapshot.bid.price', 'Bid'),
      column('prices.snapshot.ask.price', 'Ask'),
      column('prices.snapshot.ask.size', 'Ask Size'),
      column('prices', 'Gross Notional'),
      column('symbol.expiry', 'Expiry'),
    ],
  },
  {
    id: 'orderSummary',
    label: 'Order Summary',
    description: 'Main summary grid defaults',
    columns: [
      column('strategy', 'Strategy'),
      column('symbol', 'Symbol'),
      column('callPut', 'Call/Put', false),
      column('side', 'Side'),
      column('status', 'Status'),
      column('orderQty', 'Order Qty'),
      column('displayQuantity', 'Display Qty'),
      column('totalFillQty', 'Total Fill Qty'),
      column('orderPrice', 'Order Px'),
      column('averageFillPrice', 'Average Fill Px'),
      column('strikePrice', 'Strike'),
      column('orderType', 'Order Type'),
      column('traderId', 'Trader'),
      column('orderId', 'Algo/Order ID'),
      column('algoType', 'Algo Type'),
      column('exchange', 'Exchange'),
      column('memo', 'Memo', false),
      column('customText1', 'Custom Text 1'),
      column('customText2', 'Custom Text 2'),
      column('customText3', 'Custom Text 3'),
      column('platform', 'Platform'),
      column('account', 'Account'),
      column('businessUnit', 'Business Unit'),
      column('timeInForce', 'TIF'),
      column('expirationDate', 'Expiration Date'),
      column('stopPrice', 'Stop-Price'),
      column('transactionTime', 'Transaction Time'),
      column('duration', 'Duration'),
      column('endTime', 'End Time'),
      column('broker', 'Broker', false),
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    description: 'Position and greeks grid defaults',
    columns: [
      column('account', 'Strategy'),
      column('uniqueSymbol', 'Symbol'),
      column('symbol.rootSymbol', 'Ref Symbol', false),
      column('contractSymbol', 'Exchange Symbol', false),
      column('symbol.segmentMic', 'Exchange', false),
      column('symbol.secType', 'Security Type', false),
      column('currentPosition', 'Current Position'),
      column('totalTraded', 'Total Traded'),
      column('currentPrice', 'Current Price'),
      column('newPL', 'PnL (USD)'),
      column('buyTotalTraded', 'Buy Total Traded', false),
      column('buyAveragePrice', 'Avg Buy Px'),
      column('buyPL', 'Buy PnL', false),
      column('sellTotalTraded', 'Sell Total Traded', false),
      column('sellAveragePrice', 'Avg Sell Px'),
      column('sellPL', 'Sell PnL', false),
      column('businessUnit', 'Business Unit', false),
      column('delta', 'Delta'),
      column('gamma', 'Gamma'),
      column('vega', 'Vega'),
      column('theta', 'Theta'),
    ],
  },
  {
    id: 'tickerGrid',
    label: 'Ticker Grid',
    description: 'Watchlist grid defaults',
    columns: [
      column('name', 'Symbol'),
      column('exchangeSymbol', 'Exchange Symbol', false),
      column('product.description', 'Description'),
      column('prices.lastTrade.price', 'Last'),
      column('change', 'Change'),
      column('changePercent', 'Chng %'),
      column('prices.bid.price', 'Bid'),
      column('prices.bid.size', 'Bid Size'),
      column('prices.ask.price', 'Ask'),
      column('prices.ask.size', 'Ask Size'),
      column('prices.lastTrade.size', 'Last Quantity'),
      column('prices.lastTrade.dailyCumulativeVolume', 'Volume'),
      column('prices.high', 'High'),
      column('prices.low', 'Low'),
      column('prices.open', 'Open'),
      column('prices.previousDailySettlement.settlementPrice', 'Previous Settlement Price'),
      column('prices.previousDailySettlement.settlementStatus', 'Settlement Status'),
      column('prices.previousDailySettlement.settlementTimeUtc', 'Settlement Time(UTC)'),
      column('prices.openInterest', 'Open Interest'),
      column('product.expiry', 'Expiry Date'),
      column('prices.lastTrade.timeUtc', 'Time of last trade'),
      column('product.secType', 'Security Type'),
      column('product.currency', 'Currency'),
      column('product.strikePrice', 'Strike Price'),
      column('product.callPut', 'Put/Call'),
    ],
  },
  {
    id: 'orderBlotter',
    label: 'Order Blotter',
    description: 'Execution history and lifecycle grid defaults',
    columns: [
      column('traderId', 'Trader'),
      column('orderSide', 'Side'),
      column('uniqueSymbol', 'Unique Symbol'),
      column('orderPrice', 'Order Px'),
      column('orderQuantity', 'Order Qty'),
      column('account', 'Account'),
      column('executionTag', 'Strategy'),
      column('customText1', 'Custom Text 1'),
      column('customText2', 'Custom Text 2'),
      column('customText3', 'Custom Text 3'),
      column('orderType', 'Type'),
      column('timeInForce', 'TIF'),
      column('status', 'Status'),
      column('totalFilled', 'Total Fill Qty'),
      column('leavesQuantity', 'Leaves Qty'),
      column('averagePrice', 'Average Fill Px'),
      column('transactionTime', 'Transaction Time Local'),
      column('orderId', 'Order ID'),
      column('algoRunId', 'Algo ID'),
      column('displayQuantity', 'Display Qty', false),
      column('root', 'Root Symbol', false),
      column('fillQTY', 'Fill Qty', false),
      column('fillQuantity', 'Last Partial Fill Qty', false),
      column('fillPrice', 'Last Partial Fill Px', false),
      column('broker', 'Broker', false),
      column('segmentMic', 'Exch', false),
      column('orderSource', 'Platform', false),
      column('memo', 'Memo', false),
      column('strikePrice', 'Strike', false),
      column('callPut', 'Call/Put', false),
      column('clientOrderId', 'Client Order Id', false),
      column('fixSymbol', 'Fix Symbol', false),
      column('sendingTimeUTC', 'Sending Time Local', false),
      column('senderSubId', 'Sender Sub Id', false),
      column('description', 'Description', false),
      column('maturityMonthYear', 'Maturity Month/Year', false),
      column('expirationDate', 'Expiration Date', false),
      column('expireTime', 'Expire Time', false),
      column('stopPrice', 'Stop Price', false),
      column('cfiCode', 'CFI Code', false),
      column('businessUnit', 'Business Unit', false),
      column('smpId', 'SMP ID', false),
      column('internalFilledQty', 'Internal Filled Qty', false),
      column('internalAveragePrice', 'Internal Avg Price', false),
      column('venueFilledQty', 'External Filled Qty', false),
      column('venueAveragePrice', 'External Avg Price', false),
    ],
  },
  {
    id: 'options',
    label: 'Options',
    description: 'Options chain grid defaults',
    columns: [
      column('call.dailyCumulativeVolume', 'Call Volume', false),
      column('call.settlementPrice', 'Call Settle Px', false),
      column('call.vol', 'Call Mid Vol', false),
      column('call.askVol', 'Call Ask Vol', false),
      column('call.bidVol', 'Call Bid Vol', false),
      column('call.theta', 'Call Theta'),
      column('call.vega', 'Call Vega'),
      column('call.gamma', 'Call Gamma'),
      column('call.delta', 'Call Delta'),
      column('call.openInterest', 'Call Open Interest'),
      column('call.fittedVol', 'Call Fitted Vol'),
      column('call.bidSize', 'Call Bid Size'),
      column('call.bid', 'Call Bid'),
      column('call.fittedPrice', 'Call Fitted Price'),
      column('call.ask', 'Call Ask'),
      column('call.askSize', 'Call Ask Size'),
      column('strike', 'Strike'),
      column('put.bidSize', 'Put Bid Size'),
      column('put.bid', 'Put Bid'),
      column('put.fittedPrice', 'Put Fitted Price'),
      column('put.ask', 'Put Ask'),
      column('put.askSize', 'Put Ask Size'),
      column('put.fittedVol', 'Put Fitted Vol'),
      column('put.openInterest', 'Put Open Interest'),
      column('put.delta', 'Put Delta'),
      column('put.gamma', 'Put Gamma'),
      column('put.vega', 'Put Vega'),
      column('put.theta', 'Put Theta'),
      column('put.bidVol', 'Put Bid Vol', false),
      column('put.askVol', 'Put Ask Vol', false),
      column('put.vol', 'Put Mid Vol', false),
      column('put.settlementPrice', 'Put Settle Px', false),
      column('put.dailyCumulativeVolume', 'Put Volume', false),
    ],
  },
  {
    id: 'algoManager',
    label: 'Algo Manager',
    description: 'Algo monitoring grid defaults',
    columns: [
      column('algoRunId', 'Algo ID'),
      column('createdDate', 'Date'),
      column('accountInfo.traderId', 'Trader'),
      column('algoType', 'Algo Type'),
      column('algoStatus', 'Status'),
      column('symbolsWithQTY', 'Symbols'),
      column('algoMetrics.targetQuantityOverall', 'Order Quantity'),
      column('algoMetrics.tradedQuantityOverall', 'Traded Quantity'),
      column('algoMetrics', 'Remaining Quantity'),
      column('estimatedTCA', 'Estimated TCA', false),
      column('executionMetrics.runningCost.dollarsValue', 'Run Cost'),
      column('totalSaving', 'Total Saving', false),
      column('exchange', 'Exchange', false),
      column('currency', 'CCY', false),
      column('broker_code', 'Broker', false),
    ],
  },
];

export const columnPreferenceApps: ColumnPreferenceApp[] = [...columnPreferenceAppDefinitions]
  .sort((firstApp, secondApp) => firstApp.label.localeCompare(secondApp.label));

export const columnPreferenceAppsById: Record<string, ColumnPreferenceApp> = {};
for (const app of columnPreferenceApps) {
  columnPreferenceAppsById[app.id] = app;
}
